/**
 * TextField: a single-line text input (cockatrice-ui `src/ui/TextField.jsx`, ported to hyperscript; PLAN.md §0).
 * Give it label / help / error directly, or put it inside a Field.
 * Controlled: value + onChange(value, event). Uncontrolled: defaultValue.
 * `end` is a slot for a trailing action inside the box (PasswordField and SearchField use it).
 */
import './css/text-field.css';
import { mergeProps, splitProps } from 'solid-js';
import { useFieldAttrs, wrapControl } from './Field.ts';
import type { Without } from './Field.ts';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, el, spread, when, whenSome } from './solid.ts';
import type { Attributes, Child, On } from './solid.ts';

export interface TextFieldProps extends Attributes {
  /** Visible label. When given (or help/error), the control wraps itself in a Field. */
  readonly label?: Child;
  /** Help text under the control, linked with aria-describedby. */
  readonly help?: Child;
  /** Error message. Sets aria-invalid and shows the message with a warning icon. */
  readonly error?: Child;
  /** Marks the field required and shows "Required". */
  readonly required?: boolean | undefined;
  /** Shows "Optional" next to the label. */
  readonly optional?: boolean | undefined;
  /** Class for the wrapping Field. */
  readonly fieldClass?: string | undefined;
  /** Input type; sets the phone keyboard. */
  readonly type?: 'text' | 'email' | 'number' | 'url' | 'tel' | 'search' | undefined;
  /** 36 / 40 / 46 px tall. */
  readonly size?: 'sm' | 'md' | 'lg' | undefined;
  /** Controlled value. */
  readonly value?: string | undefined;
  /** Uncontrolled starting value. */
  readonly defaultValue?: string | undefined;
  /** Called on every keystroke. */
  readonly onChange?: ((value: string, event: InputEvent) => void) | undefined;
  /** The input's own input handler, run before onChange. */
  readonly onInput?: ((event: InputEvent) => void) | undefined;
  /** Called once the value is committed (the input's change event: Enter, or leaving the field), not per keystroke. */
  readonly onCommit?: ((value: string, event: Event) => void) | undefined;
  /** Icon at the start of the box. */
  readonly iconStart?: IconName | undefined;
  /** Text before the input (NOK, https://). Hidden from screen readers. */
  readonly prefix?: Child;
  /** Text after the input (words, px). Hidden from screen readers. */
  readonly suffix?: Child;
  /** Trailing slot inside the box for a small action. */
  readonly end?: Child;
  /** Error styling without a message. */
  readonly invalid?: boolean | undefined;
  /** Disables the input. */
  readonly disabled?: boolean | undefined;
  /** Class on the box. */
  readonly class?: string | undefined;
  /** Class on the <input>. */
  readonly inputClass?: string | undefined;
  /** Ref to the <input>. */
  readonly ref?: ((el: HTMLInputElement) => void) | undefined;
}

/** TextInput's props: a TextField's, with any input type (PasswordField's "password"). */
export interface TextInputProps extends Attributes, Without<TextFieldProps, 'type'> {
  /** Input type. Default "text". */
  readonly type?: string | undefined;
}

/** What TextField keeps for its Field; error and required go on to the input too (as the JSX passes them on). */
const FIELD_ONLY = ['label', 'help', 'optional', 'fieldClass'] as const;

export default function TextField(props: TextFieldProps): Child {
  const [, rest] = splitProps(props, FIELD_ONLY);
  return wrapControl(props, () => component(TextInput, rest));
}

function TextInput(props: TextInputProps): Child {
  const p = mergeProps({ type: 'text', size: 'md' }, props);
  const [local, rest] = splitProps(p, [
    'id',
    'type',
    'size',
    'value',
    'defaultValue',
    'onChange',
    'onInput',
    'onCommit',
    'iconStart',
    'prefix',
    'suffix',
    'end',
    'error',
    'invalid',
    'required',
    'disabled',
    'class',
    'inputClass',
    'ref',
    'aria-describedby',
  ]);
  const f = useFieldAttrs(local);

  const onInput = (event: On<InputEvent, HTMLInputElement>): void => {
    local.onInput?.(event);
    local.onChange?.(event.currentTarget.value, event);
  };

  const cls = (): string =>
    ['ui-text-field', `ui-text-field--${local.size}`, f.invalid() && 'is-invalid', f.disabled() && 'is-disabled', local.class].filter(Boolean).join(' ');

  return el(
    'div',
    { class: cls },
    whenSome(
      () => local.iconStart,
      (iconStart) =>
        component(Icon, {
          get name() {
            return iconStart();
          },
          size: 'sm',
          class: 'ui-text-field__icon',
        }),
    ),
    when(
      () => Boolean(local.prefix),
      () => el('span', { class: 'ui-text-field__affix', 'aria-hidden': 'true' }, () => local.prefix),
    ),
    el(
      'input',
      spread(rest, {
        ref: (node: HTMLInputElement) => {
          local.ref?.(node);
        },
        id: () => f.id(),
        type: () => local.type,
        class: () => ['ui-text-field__input', local.inputClass].filter(Boolean).join(' '),
        value: () => local.value ?? local.defaultValue ?? '',
        onInput,
        onChange: (event: On<Event, HTMLInputElement>) => {
          local.onCommit?.(event.currentTarget.value, event);
        },
        required: () => f.required(),
        disabled: () => f.disabled(),
        'aria-invalid': () => (f.invalid() ? 'true' : undefined),
        'aria-describedby': () => f.describedBy(),
      }),
    ),
    when(
      () => Boolean(local.suffix),
      () => el('span', { class: 'ui-text-field__affix', 'aria-hidden': 'true' }, () => local.suffix),
    ),
    when(
      () => Boolean(local.end),
      () => el('span', { class: 'ui-text-field__end' }, () => local.end),
    ),
  );
}

export { TextInput };
